import React from 'react'

/**
 * RELATIONSHIP MAPPINGS v4.9
 * Added reciprocal logic for Friends, Colleagues, and Boss/Employee relationships.
 */

export const getRelationshipBasedOnSex = (type, sex) => {
  if (!type) return '';
  const s = (sex || 'other').toLowerCase();
  
  const mappings = {
    'parent': { male: 'father', female: 'mother', other: 'parent' },
    'child': { male: 'son', female: 'daughter', other: 'child' },
    'sibling': { male: 'brother', female: 'sister', other: 'sibling' },
    'spouse': { male: 'husband', female: 'wife', other: 'spouse' },
    'grandparent': { male: 'grandfather', female: 'grandmother', other: 'grandparent' },
    'grandchild': { male: 'grandson', female: 'granddaughter', other: 'grandchild' },
    'parental_sibling': { male: 'uncle', female: 'aunt', other: 'aunt/uncle' },
    'sibling_child': { male: 'nephew', female: 'niece', other: 'niece/nephew' },
    'cousin': { male: 'first cousin', female: 'first cousin', other: 'first cousin' },
    'second_cousin': { male: 'second cousin', female: 'second cousin', other: 'second cousin' },
    'third_cousin': { male: 'third cousin', female: 'third cousin', other: 'third cousin' },
    
    // Direct in-laws keep the "-in-law" suffix
    'parent_in_law': { male: 'father-in-law', female: 'mother-in-law', other: 'parent-in-law' },
    'child_in_law': { male: 'son-in-law', female: 'daughter-in-law', other: 'child-in-law' },
    'sibling_in_law': { male: 'brother-in-law', female: 'sister-in-law', other: 'sibling-in-law' },
    'grandparent_in_law': { male: 'grandfather-in-law', female: 'grandmother-in-law', other: 'grandparent-in-law' },
    'grandchild_in_law': { male: 'grandson-in-law', female: 'granddaughter-in-law', other: 'grandchild-in-law' },
    
    'ex_spouse': { male: 'ex-husband', female: 'ex-wife', other: 'ex-spouse' },
    'grandparent_sibling': { male: 'great-uncle', female: 'great-aunt', other: 'great-aunt/uncle' },
    'grandsibling_child': { male: 'great-nephew', female: 'great-niece', other: 'great-niece/nephew' },
    
    // --- PRECISE TITLES FOR EXTENDED IN-LAWS ---
    'sibling_child_in_law': { male: 'nephew-in-law', female: 'niece-in-law', other: 'nephew-in-law/niece-in-law' },
    'parental_sibling_in_law': { male: 'uncle-in-law', female: 'aunt-in-law', other: 'aunt-in-law/uncle-in-law' },
    'cousin_in_law': { male: 'cousin-in-law', female: 'cousin-in-law', other: 'cousin-in-law' },
    'grandsibling_child_in_law': { male: 'great-nephew-in-law', female: 'great-niece-in-law', other: 'great-niece-in-law/nephew-in-law' },
    'grandparent_sibling_in_law': { male: 'great-uncle-in-law', female: 'great-aunt-in-law', other: 'great-aunt-in-law/uncle-in-law' },
    
    'great_grandparent': { male: 'great-grandfather', female: 'great-grandmother', other: 'great-grandparent' },
    'great_grandchild': { male: 'great-grandson', female: 'great-granddaughter', other: 'great-grandchild' },
    
    // 1st Generation Step Relationships
    'step_parent': { male: 'stepfather', female: 'stepmother', other: 'step-parent' },
    'step_child': { male: 'stepson', female: 'stepdaughter', other: 'step-child' },
    'step_sibling': { male: 'step-brother', female: 'step-sister', other: 'step-sibling' },
    
    'half_sibling': { male: 'half-brother', female: 'half-sister', other: 'half-sibling' },
    'cousin_once_removed': { male: 'first cousin once removed', female: 'first cousin once removed', other: 'first cousin once removed' },

    // Professional & Personal (v4.9)
    'friend': { male: 'friend', female: 'friend', other: 'friend' },
    'colleague': { male: 'colleague', female: 'colleague', other: 'colleague' },
    'boss': { male: 'boss', female: 'boss', other: 'boss' },
    'employee': { male: 'employee', female: 'employee', other: 'employee' }
  };

  const base = mappings[type];
  if (base) return base[s] || base.other;
  
  return type.replace(/_/g, ' ');
};

export const getBaseRelationshipType = (specificType) => {
  if (!specificType) return '';
  // V4.8 FIX: Replace BOTH hyphens and underscores with spaces to guarantee a match
  const t = specificType.toLowerCase().replace(/[-_]/g, ' ').replace(/'s/g, '').trim();
  
  const reverseMap = {
    father: 'parent', mother: 'parent',
    son: 'child', daughter: 'child',
    brother: 'sibling', sister: 'sibling',
    husband: 'spouse', wife: 'spouse',
    grandfather: 'grandparent', grandmother: 'grandparent',
    grandson: 'grandchild', granddaughter: 'grandchild',
    uncle: 'parental_sibling', aunt: 'parental_sibling',
    nephew: 'sibling_child', niece: 'sibling_child',
    'first cousin': 'cousin',
    'father in law': 'parent_in_law', 'mother in law': 'parent_in_law',
    'son in law': 'child_in_law', 'daughter in law': 'child_in_law',
    'brother in law': 'sibling_in_law', 'sister in law': 'sibling_in_law',
    'great uncle': 'grandparent_sibling', 'great aunt': 'grandparent_sibling',
    'great nephew': 'grandsibling_child', 'great niece': 'grandsibling_child',
    'great grandfather': 'great_grandparent', 'great grandmother': 'great_grandparent',
    'great grandson': 'great_grandchild', 'great granddaughter': 'great_grandchild',
    
    // Extended in-law versions
    'nephew in law': 'sibling_child_in_law', 'niece in law': 'sibling_child_in_law',
    'uncle in law': 'parental_sibling_in_law', 'aunt in law': 'parental_sibling_in_law',
    'great nephew in law': 'grandsibling_child_in_law', 'great niece in law': 'grandsibling_child_in_law',
    'great uncle in law': 'grandparent_sibling_in_law', 'great aunt in law': 'grandparent_sibling_in_law',
    'grandfather in law': 'grandparent_in_law', 'grandmother in law': 'grandparent_in_law',
    'grandson in law': 'grandchild_in_law', 'granddaughter in law': 'grandchild_in_law',
    'cousin in law': 'cousin_in_law',
    
    'second cousin': 'second_cousin', 
    'third cousin': 'third_cousin',

    // Step relationships
    stepfather: 'step_parent', stepmother: 'step_parent',
    'step father': 'step_parent', 'step mother': 'step_parent',
    'step parent': 'step_parent',
    stepson: 'step_child', stepdaughter: 'step_child',
    'step son': 'step_child', 'step daughter': 'step_child',
    'step child': 'step_child',
    'step brother': 'step_sibling', 'step sister': 'step_sibling',
    'step sibling': 'step_sibling',

    // Half relationships
    'half brother': 'half_sibling', 'half sister': 'half_sibling',
    'half sibling': 'half_sibling',
    halfbrother: 'half_sibling', halfsister: 'half_sibling',

    'first cousin once removed': 'cousin_once_removed',

    // Professional & Personal (v4.9)
    'friend': 'friend',
    'colleague': 'colleague',
    'boss': 'boss',
    'employee': 'employee'
  };

  if (reverseMap[t]) return reverseMap[t];

  return t.replace(/ /g, '_');
};

export const getOppositeRelationship = (relationship, sex) => {
  const base = getBaseRelationshipType(relationship);
  
  const oppositeMap = {
    'parent': 'child',
    'child': 'parent',
    'sibling': 'sibling',
    'spouse': 'spouse',
    'grandparent': 'grandchild',
    'grandchild': 'grandparent',
    'parental_sibling': 'sibling_child',
    'sibling_child': 'parental_sibling',
    'parent_in_law': 'child_in_law',
    'child_in_law': 'parent_in_law',
    'sibling_in_law': 'sibling_in_law',
    'cousin': 'cousin',
    'second_cousin': 'second_cousin',
    'third_cousin': 'third_cousin',
    'ex_spouse': 'ex_spouse',
    'step_parent': 'step_child',
    'step_child': 'step_parent',
    'step_sibling': 'step_sibling',
    'half_sibling': 'half_sibling',
    'grandparent_sibling': 'grandsibling_child',
    'grandsibling_child': 'grandparent_sibling',
    'sibling_child_in_law': 'parental_sibling_in_law', 
    'parental_sibling_in_law': 'sibling_child_in_law',
    'grandparent_sibling_in_law': 'grandsibling_child_in_law',
    'grandsibling_child_in_law': 'grandparent_sibling_in_law',
    'grandparent_in_law': 'grandchild_in_law',
    'grandchild_in_law': 'grandparent_in_law',
    'cousin_in_law': 'cousin_in_law',
    'great_grandparent': 'great_grandchild',
    'great_grandchild': 'great_grandparent',
    'cousin_once_removed': 'cousin_once_removed',

    // Professional & Personal (v4.9)
    'friend': 'friend',
    'colleague': 'colleague',
    'boss': 'employee',
    'employee': 'boss'
  };

  const oppBase = oppositeMap[base] || base;
  return sex ? getRelationshipBasedOnSex(oppBase, sex) : oppBase;
};